import { useState } from "react";
import { api } from "../api/client";
import { useI18n } from "../i18n/I18nProvider";
import {
  AlertBanner,
  Button,
  Card,
  CodeBlock,
  Divider,
  FieldShell,
  Input,
  PlayIcon,
  SegmentedControl,
  typographyClass,
} from "../ui-kit";

/** Два способа запустить один и тот же фрейм. */
type RunMode = "id" | "token";

export function FramesPage() {
  const { t } = useI18n();
  const [mode, setMode] = useState<RunMode>("id");
  const [frameId, setFrameId] = useState("");
  const [runId, setRunId] = useState("");
  const [token, setToken] = useState("");
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);

  const call = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      setResult(await action());
    } catch (cause) {
      setResult(null);
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  return (
    <Card>
      <h1 className={typographyClass("header-24-semibold")}>{t("frames.title")}</h1>
      <p className={typographyClass("text-14-medium")} style={{ color: "var(--color-text-secondary-v2)" }}>
        {t("frames.subtitle")}
      </p>

      {/* Дизайн-ревью: три кнопки в ряд читались как равноценные, хотя это не так.
          Запуск по id и по токену — два взаимоисключающих способа сделать одно и то
          же, поэтому это переключатель с одной кнопкой запуска. Проверка статуса —
          отдельное действие над уже созданным запуском, и живёт отдельным блоком. */}
      <SegmentedControl<RunMode>
        value={mode}
        onChange={setMode}
        items={[
          { value: "id", label: t("frames.byId") },
          { value: "token", label: t("frames.byToken") },
        ]}
      />

      {mode === "id" ? (
        <FieldShell label={t("frames.frameId")} htmlFor="frame-id">
          <Input
            id="frame-id"
            value={frameId}
            placeholder="3fa85f64-5717-4562-b3fc-2c963f66afa6"
            onChange={(event) => setFrameId(event.target.value)}
          />
        </FieldShell>
      ) : (
        <FieldShell label={t("frames.token")} htmlFor="frame-token">
          <Input
            id="frame-token"
            type="password"
            value={token}
            onChange={(event) => setToken(event.target.value)}
          />
        </FieldShell>
      )}

      <div>
        <Button
          endIcon={<PlayIcon />}
          disabled={mode === "id" ? !frameId : !token}
          onClick={() =>
            call(() => (mode === "id" ? api.runFrame(frameId) : api.runFrameByToken(token)))
          }
        >
          {t("action.run")}
        </Button>
      </div>

      <Divider orientation="horizontal" />

      <h2 className={typographyClass("header-16-semibold")}>{t("frames.status")}</h2>

      {/* Два поля рядом, пока они помещаются: на узком экране колонка одна,
          иначе оба id сжимались бы до нечитаемой ширины. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: "var(--spacing-3)" }}>
        <FieldShell label={t("frames.frameId")} htmlFor="status-frame-id">
          <Input id="status-frame-id" value={frameId} onChange={(event) => setFrameId(event.target.value)} />
        </FieldShell>
        <FieldShell label={t("frames.runId")} htmlFor="run-id">
          <Input id="run-id" value={runId} onChange={(event) => setRunId(event.target.value)} />
        </FieldShell>
      </div>

      <div>
        <Button
          variant="tertiary"
          disabled={!frameId || !runId}
          onClick={() => call(() => api.frameRun(frameId, runId))}
        >
          {t("action.check")}
        </Button>
      </div>

      {error && <AlertBanner tone="error" title={t("status.error")}>{error}</AlertBanner>}
      {result != null && <CodeBlock code={JSON.stringify(result, null, 2)} copyLabel={t("action.copy")} />}
    </Card>
  );
}
